import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';

/**
 * Error boundary do app. É classe porque o React só expõe
 * getDerivedStateFromError/componentDidCatch nesse formato — única exceção
 * à regra "sem classes" do projeto.
 *
 * `resetKey`: quando muda, o boundary volta a renderizar os filhos. Nas rotas
 * é o pathname, então navegar pra outra página sai do estado de erro sozinho.
 */
type ErrorBoundaryProps = {
    children: ReactNode;
    fallback: (props: { error: Error; reset: () => void }) => ReactNode;
    resetKey?: string;
    scope: string;
};

type ErrorBoundaryState = { error: Error | null };

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { error: null };

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        Sentry.captureException(error, {
            tags: { boundary: this.props.scope },
            contexts: { react: { componentStack: info.componentStack ?? '' } },
        });
    }

    componentDidUpdate(prev: ErrorBoundaryProps): void {
        if (this.state.error && prev.resetKey !== this.props.resetKey) {
            this.reset();
        }
    }

    reset = (): void => {
        this.setState({ error: null });
    };

    render(): ReactNode {
        if (this.state.error) {
            return this.props.fallback({ error: this.state.error, reset: this.reset });
        }

        return this.props.children;
    }
}

const IconAlert = () => (
    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

type FallbackProps = { error: Error; reset: () => void; title: string; description: string };

const ErrorFallback = ({ error, reset, title, description }: FallbackProps) => (
    <div className="panel max-w-xl mx-auto mt-8 text-center" role="alert">
        <div className="text-danger flex justify-center mb-3">
            <IconAlert />
        </div>
        <h2 className="text-base font-semibold mb-1">{title}</h2>
        <p className="text-white-dark mb-5">{description}</p>
        {import.meta.env.DEV && (
            <pre className="text-left text-xs bg-white-light dark:bg-surface-input dark:border-surface-border border rounded p-3 mb-5 overflow-x-auto whitespace-pre-wrap">{error.message}</pre>
        )}
        <div className="flex items-center justify-center gap-3 flex-wrap">
            <button type="button" onClick={reset} className="btn btn-primary btn-sm">
                Tentar de novo
            </button>
            <a href="/apps" className="btn btn-outline-primary btn-sm">
                Ir para o início
            </a>
        </div>
    </div>
);

/**
 * Boundary de página: envolve só o elemento da rota, dentro do layout.
 * Header, sidebar e navbar continuam de pé quando uma página quebra.
 */
export const RouteErrorBoundary = ({ children }: { children: ReactNode }) => {
    const { pathname } = useLocation();

    return (
        <ErrorBoundary
            scope="route"
            resetKey={pathname}
            fallback={({ error, reset }) => (
                <ErrorFallback
                    error={error}
                    reset={reset}
                    title="Algo deu errado nesta página"
                    description="O erro foi registrado e a equipe de TI será avisada. Tente de novo ou volte para o início."
                />
            )}
        >
            {children}
        </ErrorBoundary>
    );
};

/**
 * Último recurso, acima do router. Não tem layout nem router disponíveis
 * aqui, por isso a saída é recarregar a página.
 */
export const AppErrorBoundary = ({ children }: { children: ReactNode }) => (
    <ErrorBoundary
        scope="app"
        fallback={({ error }) => (
            <div className="min-h-screen flex items-center px-4 bg-white dark:bg-surface text-black dark:text-white-dark font-figtree text-sm">
                <ErrorFallback
                    error={error}
                    reset={() => window.location.reload()}
                    title="O painel encontrou um erro inesperado"
                    description="O erro foi registrado. Recarregue a página para continuar."
                />
            </div>
        )}
    >
        {children}
    </ErrorBoundary>
);
